import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

declare const pdfjsLib: any;

const PDFReaderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookTitle = searchParams.get('title') || 'Book';
  const pdfUrl = searchParams.get('pdf') || '';

  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentSpread, setCurrentSpread] = useState(0);
  const [currentView] = useState<'book' | 'scroll'>('book');
  const [isFlipping, setIsFlipping] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [pdfWidth, setPdfWidth] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const headerTimeoutRef = useRef<number | null>(null);

  const canvasLeftRef = useRef<HTMLCanvasElement>(null);
  const canvasRightRef = useRef<HTMLCanvasElement>(null);
  const canvasFlipFrontRef = useRef<HTMLCanvasElement>(null);
  const canvasFlipBackRef = useRef<HTMLCanvasElement>(null);
  const flipPageRef = useRef<HTMLDivElement>(null);
  const shadowLeftRef = useRef<HTMLDivElement>(null);
  const shadowRightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.onload = () => {
      (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      if (pdfUrl) loadPDF(pdfUrl);
    };
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, [pdfUrl]);

  const loadPDF = async (url: string) => {
    try {
      const loadingTask = (window as any).pdfjsLib.getDocument({
        url,
        withCredentials: false,
        isEvalSupported: false,
      });
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setCurrentSpread(0);
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Failed to load PDF. Please check the file URL.');
    }
  };

  const renderPageToCanvas = async (pageNum: number, canvas: HTMLCanvasElement | null) => {
    if (!canvas || !pdfDoc || pageNum < 1 || pageNum > totalPages) {
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          canvas.width = canvas.parentElement?.offsetWidth || 400;
          canvas.height = canvas.parentElement?.offsetHeight || 560;
          ctx.fillStyle = '#faf7f2';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }
      return;
    }
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.8 });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    setPdfWidth(Math.round(viewport.width));
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  };

  const renderStaticSpread = async () => {
    const leftPage = currentSpread + 1;
    const rightPage = currentSpread + 2;
    await Promise.all([
      renderPageToCanvas(leftPage, canvasLeftRef.current),
      renderPageToCanvas(rightPage, canvasRightRef.current),
    ]);
  };

  useEffect(() => {
    if (pdfDoc) renderStaticSpread();
  }, [pdfDoc, currentSpread]);

  const changePage = async (dir: number) => {
    if (!pdfDoc || isFlipping) return;
    const maxSpread = totalPages % 2 === 0 ? totalPages - 2 : totalPages - 1;
    const nextSpread = currentSpread + dir * 2;
    if (nextSpread < 0 || nextSpread > maxSpread) return;

    setIsFlipping(true);
    const flipPage = flipPageRef.current;
    const shadowL = shadowLeftRef.current;
    const shadowR = shadowRightRef.current;

    if (dir === 1) {
      const curRight = currentSpread + 2;
      const nextLeft = nextSpread + 1;
      const nextRight = nextSpread + 2;

      await Promise.all([
        renderPageToCanvas(curRight, canvasFlipFrontRef.current),
        renderPageToCanvas(nextLeft, canvasFlipBackRef.current),
        renderPageToCanvas(nextRight, canvasRightRef.current),
      ]);

      if (flipPage) {
        flipPage.style.right = '0';
        flipPage.style.left = 'unset';
        flipPage.className = 'flip-page flip-forward';
      }
      shadowR?.classList.add('visible');

      setTimeout(async () => {
        await renderPageToCanvas(nextLeft, canvasLeftRef.current);
        shadowR?.classList.remove('visible');
        shadowL?.classList.add('visible');
      }, 350);
    } else {
      const curLeft = currentSpread + 1;
      const prevLeft = nextSpread + 1;
      const prevRight = nextSpread + 2;

      await Promise.all([
        renderPageToCanvas(curLeft, canvasFlipFrontRef.current),
        renderPageToCanvas(prevRight, canvasFlipBackRef.current),
        renderPageToCanvas(prevLeft, canvasLeftRef.current),
      ]);

      if (flipPage) {
        flipPage.style.left = '0';
        flipPage.style.right = 'unset';
        flipPage.className = 'flip-page flip-backward';
      }
      shadowL?.classList.add('visible');

      setTimeout(async () => {
        await renderPageToCanvas(prevRight, canvasRightRef.current);
        shadowL?.classList.remove('visible');
        shadowR?.classList.add('visible');
      }, 350);
    }

    await new Promise(r => setTimeout(r, 760));
    shadowL?.classList.remove('visible');
    shadowR?.classList.remove('visible');
    if (flipPage) flipPage.className = 'flip-page';

    setCurrentSpread(nextSpread);
    setIsFlipping(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (currentView !== 'book') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') changePage(1);
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') changePage(-1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentView, currentSpread, isFlipping]);

  const leftPage = currentSpread + 1;
  const rightPage = Math.min(currentSpread + 2, totalPages);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,400&display=swap');
                .pdf-reader-page { background: rgba(0,0,0,0.3); backdrop-filter: blur(12px); min-height: 100vh; font-family: 'Crimson Pro', Georgia, serif; }
                .pdf-topbar { position: fixed; bottom: 0; left: 0; right: 0; z-index: 200; display: flex; align-items: center; justify-content: space-between; padding: 0 32px; height: 64px; background: #ffffff; box-shadow: 0 -2px 8px rgba(0,0,0,0.1); }
        .pdf-brand { font-family: 'Playfair Display', serif; font-size: 1.4rem; color: #333; letter-spacing: 0.08em; text-shadow: none; }
        .pdf-close-btn { background: rgba(0,0,0,0.05); border: 1px solid rgba(0,0,0,0.15); border-radius: 40px; color: #333; padding: 8px 18px; cursor: pointer; transition: all 0.25s; display: flex; align-items: center; gap: 8px; }
        .pdf-close-btn:hover { background: rgba(0,0,0,0.12); }
        .book-scene { perspective: 2500px; width: 100vw; max-width: 100vw; height: calc(100vh - 120px); aspect-ratio: 2 / 1.3; margin: 0 auto; }
        .book-spread { position: relative; width: 100%; height: 100%; display: flex; border-radius: 3px 10px 10px 3px; box-shadow: -6px 0 12px rgba(0,0,0,0.6), 0 30px 90px rgba(0,0,0,0.8); background: #faf7f2; transform-style: preserve-3d; }
        .page-static { flex: 1; position: relative; overflow: hidden; }
        .page-static-left { background: #faf7f2; border-radius: 3px 0 0 3px; }
        .page-static-right { background: #faf7f2; border-radius: 0 10px 10px 0; }
        .page-static canvas { width: 100% !important; height: 100% !important; display: block; }
        .book-spread::before { content: ''; position: absolute; left: 50%; top: 0; bottom: 0; width: 10px; transform: translateX(-50%); background: linear-gradient(to right, rgba(0,0,0,0.28), rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.05) 60%, rgba(0,0,0,0.22)); z-index: 50; pointer-events: none; }
        .flip-page { position: absolute; top: 0; width: 50%; height: 100%; transform-style: preserve-3d; z-index: 100; pointer-events: none; display: none; }
        .flip-page.flip-forward { right: 0; transform-origin: left center; display: block; animation: flipForward 0.7s cubic-bezier(0.645, 0.045, 0.355, 1.000) forwards; }
        .flip-page.flip-backward { left: 0; transform-origin: right center; display: block; animation: flipBackward 0.7s cubic-bezier(0.645, 0.045, 0.355, 1.000) forwards; }
        @keyframes flipForward { from { transform: rotateY(0deg); } to { transform: rotateY(-180deg); } }
        @keyframes flipBackward { from { transform: rotateY(0deg); } to { transform: rotateY(180deg); } }
        .flip-front, .flip-back { position: absolute; inset: 0; backface-visibility: hidden; overflow: hidden; }
        .flip-front { background: #faf7f2; transform: rotateY(0deg); }
        .flip-back { background: #f0ebe0; transform: rotateY(180deg); }
        .flip-front canvas, .flip-back canvas { width: 100% !important; height: 100% !important; display: block; }
        .shadow-overlay { position: absolute; top: 0; bottom: 0; width: 55%; z-index: 60; pointer-events: none; opacity: 0; transition: opacity 0.15s; }
        .shadow-overlay.left { left: 0; background: linear-gradient(to right, rgba(0,0,0,0.3), transparent); }
        .shadow-overlay.right { right: 0; background: linear-gradient(to left, rgba(0,0,0,0.3), transparent); }
        .shadow-overlay.visible { opacity: 1; }
        .page-num { position: absolute; bottom: 12px; right: 16px; font-size: 0.75rem; color: #6b5e4e; letter-spacing: 0.05em; z-index: 5; }
        .pdf-nav-btn { position: fixed; top: 50%; transform: translateY(-50%); background: rgba(201,168,76,0.12); border: 1px solid rgba(201,168,76,0.25); color: #c9a84c; width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.25s; z-index: 150; backdrop-filter: blur(8px); }
        .pdf-nav-btn:hover { background: rgba(201,168,76,0.25); border-color: #c9a84c; }
        .pdf-nav-btn:disabled { opacity: 0.2; pointer-events: none; }
        .pdf-nav-btn.left { left: 16px; }
        .pdf-nav-btn.right { right: 16px; }
        .page-indicator { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); color: rgba(255,255,255,0.35); font-size: 0.85rem; letter-spacing: 0.1em; z-index: 150; }
      `}</style>

      <div className="pdf-reader-page" onMouseMove={() => {
        // Optional: uncomment below for auto-hide behavior
        // if (headerTimeoutRef.current) clearTimeout(headerTimeoutRef.current);
        // headerTimeoutRef.current = setTimeout(() => setShowHeader(false), 3000);
      }} onMouseLeave={() => {
        // Optional: uncomment below for auto-hide behavior
        // if (headerTimeoutRef.current) clearTimeout(headerTimeoutRef.current);
      }}>
        <header className="pdf-topbar\" style={{ bottom: showHeader ? 0 : '-80px', borderTop: '1px solid rgba(0,0,0,0.1)', borderBottom: 'none', opacity: showHeader ? 1 : 0.7, transform: showHeader ? 'translateY(0)' : 'translateY(80px)', transition: 'all 0.3s ease' }}>
          <div className="pdf-brand">📖 {bookTitle} {pdfWidth > 0 && <span style={{ fontSize: '0.8rem', marginLeft: '12px', color: '#999' }}>({pdfWidth}px)</span>}</div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => setZoomLevel(Math.max(0.8, zoomLevel - 0.2))}
              style={{
                padding: '8px 12px',
                background: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                color: '#333'
              }}
            >
              −
            </button>
            <span style={{ fontSize: '0.8rem', minWidth: '45px', textAlign: 'center', color: '#666' }}>
              {Math.round(zoomLevel * 100)}%
            </span>
            <button
              onClick={() => setZoomLevel(Math.min(2.0, zoomLevel + 0.2))}
              style={{
                padding: '8px 12px',
                background: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                color: '#333'
              }}
            >
              +
            </button>
          </div>
          <button
            onClick={() => setShowHeader(!showHeader)}
            style={{
              padding: '8px 16px',
              background: '#f0f0f0',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              color: '#333'
            }}
          >
            {showHeader ? '▼ Hide' : '▲ Show'}
          </button>
          <button onClick={() => navigate(-1)} className="pdf-close-btn">
            <X size={16} />
            Close
          </button>
        </header>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '40px 20px', overflow: 'auto' }}>
          <div className="book-scene" style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center', transition: 'transform 0.2s ease' }}>
            <div className="book-spread">
              <div className="page-static page-static-left">
                <canvas ref={canvasLeftRef} />
              </div>
              <div className="page-static page-static-right">
                <canvas ref={canvasRightRef} />
                <div className="page-num">{rightPage <= totalPages ? rightPage : leftPage}</div>
              </div>
              <div ref={flipPageRef} className="flip-page">
                <div className="flip-front">
                  <canvas ref={canvasFlipFrontRef} />
                </div>
                <div className="flip-back">
                  <canvas ref={canvasFlipBackRef} />
                </div>
              </div>
              <div ref={shadowLeftRef} className="shadow-overlay left" />
              <div ref={shadowRightRef} className="shadow-overlay right" />
            </div>
          </div>
        </div>

        <button onClick={() => changePage(-1)} disabled={isFlipping || currentSpread <= 0} className="pdf-nav-btn left">
          <ChevronLeft size={20} />
        </button>
        <button onClick={() => changePage(1)} disabled={isFlipping || currentSpread + 2 >= totalPages} className="pdf-nav-btn right">
          <ChevronRight size={20} />
        </button>
        <div className="page-indicator">
          {leftPage === rightPage ? `${leftPage} / ${totalPages}` : `${leftPage}–${rightPage} / ${totalPages}`}
        </div>
      </div>
    </>
  );
};

export default PDFReaderPage;
